import { useCallback } from "react";

import { useStores } from "@/stores";
import { useIsProjectScopedMember } from "@/hooks/buildings/useIsProjectScopedMember";
import type { BuildingViewMode } from "@/stores/ui/buildingViewModeStore";

export type { BuildingViewMode };

export interface UseBuildingViewModeResult {
  /**
   * The active sidebar view mode for this building ("building" by default;
   * always "projects" for a project-scoped member).
   */
  viewMode: BuildingViewMode;
  /** Remember a new view mode for this building. */
  setViewMode: (mode: BuildingViewMode) => void;
}

/**
 * The sidebar "Building / Projects" view-mode for one building — persisted
 * per building in `buildingViewModeStore` (localStorage-backed).
 *
 * Side-effect free: navigation and scope handling on mode change live in the
 * toggle component / BuildingLayout, not here.
 *
 * A project-scoped member (a subcontractor without an owner/admin role) has
 * no Building view: the mode reads "projects" for them and a switch to
 * "building" is ignored, so no route-sync or toggle path can land them there.
 *
 * Must be used inside an `observer` component — it reads MST observables.
 */
export function useBuildingViewMode(buildingId: string): UseBuildingViewModeResult {
  const root = useStores();
  const viewModeStore = root.buildingViewModeStore;

  const projectsOnly = useIsProjectScopedMember();

  const storedMode = buildingId ? viewModeStore.getViewMode(buildingId) : "building";
  const viewMode: BuildingViewMode = projectsOnly ? "projects" : storedMode;

  const setViewMode = useCallback(
    (mode: BuildingViewMode) => {
      if (!buildingId) return;
      if (projectsOnly && mode === "building") return;
      viewModeStore.setViewMode(buildingId, mode);
    },
    [buildingId, viewModeStore, projectsOnly]
  );

  return { viewMode, setViewMode };
}
